import { queryOptions } from "@tanstack/react-query";
import {
  GuideUnavailableError,
  loadLocationDetails,
  NotFoundError,
  SlugRedirectError,
  syncAccessibility,
  syncAccessibilityEnterprise,
} from "../../../apis/venueDetails";
import type { VenueDetailsBySlugResponse } from "./useVenueDetailsBySlug";

export type VenueQueryOptionsParams = {
  slug: string;
  latitude: string;
  longitude: string;
  accessToken: string | null | undefined;
  type?: "Basic" | "Complex";
  syncSpaces?: boolean;
  password?: string;
  passwordToken?: string;
  ssoToken?: string;
  /** Web SSR venue route only — surfaces a retired-slug 301 as a SlugRedirectError. */
  throwOnSlugRedirect?: boolean;
  /** SSR loader's time-limit abort. Not TanStack's context signal: reading that makes every
   * observer (native, portals) cancel the fetch on unmount. Not part of the key. */
  signal?: AbortSignal;
};

/**
 * Key-identity inputs the web venue-details route may vary. The fields it must NOT
 * vary are omitted so the loader and the page component cannot supply them differently.
 */
export type VenueDetailsRouteQueryParams = Omit<
  VenueQueryOptionsParams,
  "throwOnSlugRedirect" | "type" | "syncSpaces"
>;

/**
 * Query options for the web venue-details route — the single definition shared by its
 * SSR loader and its page component.
 *
 * Both must resolve to the same query key: the loader primes the cache during SSR and
 * the component reads that entry on its first render. Any divergence is a cache miss,
 * and the page server-renders with no body content.
 *
 * @param params Slug, coordinates, access token and access-gate tokens.
 * @returns Query options whose key both call sites are guaranteed to agree on.
 */
export function venueDetailsRouteQueryOptions(
  params: VenueDetailsRouteQueryParams,
) {
  return venueQueryOptions({ ...params, throwOnSlugRedirect: true });
}

/**
 * Query options for the web what's-nearby route — the single definition shared by its SSR
 * loader and its page component, so both address the same cache entry.
 *
 * @param params Slug, coordinates, access token and access-gate tokens.
 * @returns Query options for the area map's details, fetched as a Complex venue.
 */
export function whatsNearbyRouteQueryOptions(
  params: VenueDetailsRouteQueryParams,
) {
  return venueQueryOptions({
    ...params,
    type: "Complex",
    throwOnSlugRedirect: true,
  });
}

export function venueQueryOptions({
  slug,
  latitude,
  longitude,
  accessToken,
  type,
  syncSpaces = true,
  password,
  passwordToken,
  ssoToken,
  throwOnSlugRedirect,
  signal,
}: VenueQueryOptionsParams) {
  return queryOptions({
    queryKey: [
      "loadVenueDetailsBySlug",
      slug,
      latitude,
      longitude,
      accessToken,
      type,
      syncSpaces,
      password,
      passwordToken,
      ssoToken,
      throwOnSlugRedirect,
    ] as const,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: false,
    // Deterministic outcomes are never worth retrying: a retired-slug 301
    // (SlugRedirectError), a genuine 404 (NotFoundError) and a not-live guide 410
    // (GuideUnavailableError) resolve identically on every attempt, so retrying only
    // delays the caller's handling. Real network failures keep the default 3 attempts.
    retry: (failureCount, error) => {
      if (
        error instanceof SlugRedirectError ||
        error instanceof NotFoundError ||
        error instanceof GuideUnavailableError
      ) {
        return false;
      }
      return failureCount < 3;
    },
    queryFn: async (): Promise<VenueDetailsBySlugResponse> => {
      const detailsResponse = await loadLocationDetails(slug, {
        latitude,
        longitude,
        token: (accessToken as string) || "",
        type,
        throwOnSlugRedirect,
        signal,
      });

      const isComplexVenue = detailsResponse.templateType !== "Basic";
      // A retired slug is followed through the 301 here, but sync-spaces rejects it.
      const canonicalSlug = detailsResponse.business?.slug || slug;

      if (!syncSpaces) {
        return { details: detailsResponse } as VenueDetailsBySlugResponse;
      }

      if (isComplexVenue) {
        const syncResult = await syncAccessibilityEnterprise(
          detailsResponse.business.id,
          canonicalSlug,
          { password, passwordToken, ssoToken, accessToken, signal },
        );

        if (syncResult.canViewPage === 0) {
          return {
            details: detailsResponse,
            canViewPage: 0,
            isSsoProtect: syncResult.isSsoProtect,
            ssoTenantId: syncResult.ssoTenantId,
          } as VenueDetailsBySlugResponse;
        }

        return {
          details: detailsResponse,
          accesibilityCategoriesEnterprise:
            syncResult.accesibilityCategoriesEnterprise,
          floorTypeOptions: syncResult.floorTypeOptions,
          enterpriseBusinessNotesActions:
            syncResult.enterpriseBusinessNotesActions,
          businessNotesTasks: syncResult.businessNotesTasks,
          review_tags: syncResult.review_tags,
          businessNotesAreaLocationTypes:
            syncResult.businessNotesAreaLocationTypes,
          businessNotesForMappers: syncResult.businessNotesForMappers,
          businessNotesAll: syncResult.businessNotesAll,
          accessibility_filters: syncResult.accessibility_filters,
          canViewPage: syncResult.canViewPage,
          passwordProtectedPageToken: syncResult.passwordProtectedPageToken,
          isSsoProtect: syncResult.isSsoProtect,
          ssoTenantId: syncResult.ssoTenantId,
        } as VenueDetailsBySlugResponse;
      }

      const accesibilityCategories = await syncAccessibility(
        detailsResponse.business.id,
        canonicalSlug,
        { signal },
      );
      return {
        details: detailsResponse,
        accesibilityCategories,
      } as VenueDetailsBySlugResponse;
    },
  });
}
